import { create } from "zustand";
import { jwtDecode } from "jwt-decode";
import { PermissionsEnum } from "../constants/enums";
import {
  clearAuthSession,
  getStoredToken,
  isSessionValid,
} from "../api/authStorage";
import type { Token, UserModel } from "../types/models";
import { getCurrentUserService } from "../api/services";

type AuthState = {
  token: string | null;
  claims: Token | null;
  user: UserModel | null;
  isAuthenticated: boolean;
  isStaff: boolean;
  hydrated: boolean;
  initAuth: () => void;
  setSessionFromToken: () => boolean;
  refreshUser: () => Promise<UserModel | null>;
  logout: () => void;
  assertStaffAccess: () => boolean;
};

const decodeClaims = (token: string | null): Token | null => {
  if (!token) {
    return null;
  }
  try {
    return jwtDecode<Token>(token);
  } catch {
    return null;
  }
};

const hasStaffPermission = (permissions?: string[]) => {
  return Boolean(
    permissions?.includes(PermissionsEnum.DOCTOR) ||
      permissions?.includes(PermissionsEnum.CARE_GIVER)
  );
};

export const useAuthStore = create<AuthState>((set, get) => ({
  token: null,
  claims: null,
  user: null,
  isAuthenticated: false,
  isStaff: false,
  hydrated: false,

  initAuth: () => {
    if (!isSessionValid()) {
      clearAuthSession();
      set({
        token: null,
        claims: null,
        user: null,
        isAuthenticated: false,
        isStaff: false,
        hydrated: true,
      });
      return;
    }

    const token = getStoredToken();
    const claims = decodeClaims(token);
    const isStaff = hasStaffPermission(claims?.permissions);
    if (!isStaff) {
      clearAuthSession();
      set({
        token: null,
        claims: null,
        user: null,
        isAuthenticated: false,
        isStaff: false,
        hydrated: true,
      });
      return;
    }

    set({
      token,
      claims,
      isAuthenticated: true,
      isStaff: true,
      hydrated: true,
    });
  },

  setSessionFromToken: () => {
    if (!isSessionValid()) {
      get().logout();
      return false;
    }
    const token = getStoredToken();
    const claims = decodeClaims(token);
    const isStaff = hasStaffPermission(claims?.permissions);
    if (!isStaff) {
      get().logout();
      return false;
    }
    set({
      token,
      claims,
      isAuthenticated: true,
      isStaff: true,
    });
    return true;
  },

  assertStaffAccess: () => {
    const claims = get().claims || decodeClaims(getStoredToken());
    return hasStaffPermission(claims?.permissions);
  },

  refreshUser: async () => {
    try {
      const user = await getCurrentUserService();
      set({ user });
      return user;
    } catch {
      return null;
    }
  },

  logout: () => {
    clearAuthSession();
    set({
      token: null,
      claims: null,
      user: null,
      isAuthenticated: false,
      isStaff: false,
    });
  },
}));
